import { Modal } from "@pg-flex/shared-js/ui/Modal";
import { Spinner } from "@pg-flex/shared-js/ui/Spinner";
import { gettext } from "@pg-flex/shared-js/utils/i18n";
import { DynamicImportError } from "@pg-flex/shared-js/utils/lazy";
import React, {
  ComponentProps,
  Suspense,
  ComponentType,
  ReactNode,
} from "react";

import { FullBox } from "@/components/FullBox";
import { ScreenContainer } from "@/components/ScreenContainer";

export const lazy = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends ComponentType<any>,
  CompName extends string,
  Mod extends { [k in CompName]: T },
>(
  type: "screen" | "modal" | "box",
  importer: () => Promise<T>
) => {
  let fallbackComp: ReactNode;
  switch (type) {
    case "screen":
      fallbackComp = (
        <ScreenContainer
          title=""
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spinner />
        </ScreenContainer>
      );
      break;
    case "modal":
      fallbackComp = (
        <Modal isOpen>
          <Spinner />
        </Modal>
      );
      break;
    case "box":
      fallbackComp = <FullBox>{gettext("Loading…")}</FullBox>;
      break;
  }

  const Component = React.lazy(async () => ({
    default: await wrapImport(importer()),
  }));

  return function LazyComponent(props: ComponentProps<Mod[CompName]>) {
    return (
      <Suspense fallback={fallbackComp}>
        <Component {...props} />
      </Suspense>
    );
  };
};

// Custom version of wrapImport that doesn't expect a module
export async function wrapImport<T>(promise: Promise<T>) {
  try {
    return await promise;
  } catch (error) {
    // Create dedicated error so that the error boundary can detect this case.
    throw new DynamicImportError("Failed to load lazy module", {
      cause: error,
    });
  }
}
